import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { addDays, startOfLocalDay, toLocalDateKey } from '../utils/activityDates';

type DateMode = 'today' | 'tomorrow' | 'custom';

interface DateContextType {
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  today: Date;
  tomorrow: Date;
  dateMode: DateMode;
}

const getValidDate = (date: Date) => {
  const today = startOfLocalDay(new Date());
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return today;
  const selected = startOfLocalDay(date);
  return selected.getTime() < today.getTime() ? today : selected;
};

const DateContext = createContext<DateContextType>({
  selectedDate: startOfLocalDay(new Date()),
  setSelectedDate: () => {},
  today: startOfLocalDay(new Date()),
  tomorrow: addDays(startOfLocalDay(new Date()), 1),
  dateMode: 'today',
});

export const DateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedDateState, setSelectedDateState] = useState(() => startOfLocalDay(new Date()));
  const [today, setToday] = useState(() => startOfLocalDay(new Date()));
  const tomorrow = useMemo(() => addDays(today, 1), [today]);

  useEffect(() => {
    const interval = setInterval(() => {
      const nextToday = startOfLocalDay(new Date());
      setToday(current => (current.getTime() === nextToday.getTime() ? current : nextToday));
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const setSelectedDate = useCallback((date: Date) => {
    setSelectedDateState(current => {
      const next = getValidDate(date);
      return current.getTime() === next.getTime() ? current : next;
    });
  }, []);

  const selectedDate = useMemo(() => getValidDate(selectedDateState), [selectedDateState]);

  useEffect(() => {
    if (selectedDateState.getTime() < today.getTime()) {
      setSelectedDateState(current => (current.getTime() === today.getTime() ? current : today));
    }
  }, [selectedDateState, today]);

  const dateMode = useMemo<DateMode>(() => {
    const selectedKey = toLocalDateKey(selectedDate);
    if (selectedKey === toLocalDateKey(today)) return 'today';
    if (selectedKey === toLocalDateKey(tomorrow)) return 'tomorrow';
    return 'custom';
  }, [selectedDate, today, tomorrow]);

  const value = useMemo(
    () => ({ selectedDate, setSelectedDate, today, tomorrow, dateMode }),
    [selectedDate, setSelectedDate, today, tomorrow, dateMode],
  );

  return (
    <DateContext.Provider value={value}>
      {children}
    </DateContext.Provider>
  );
};

export const useDateContext = () => useContext(DateContext);
